"use client"

import { HttpTypes } from "@medusajs/types"
import { ProductVariants } from "@/components/molecules"
import useGetAllSearchParams from "@/hooks/useGetAllSearchParams"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { buildSelectedVariantOptions, resolveVariantId } from "@/lib/utils"
import { useState } from "react"
import { Chat } from "@/components/organisms/Chat/Chat"
import { SellerProps } from "@/types/seller"
import { WishlistButton } from "../WishlistButton/WishlistButton"
import { Wishlist } from "@/types/wishlist"
import { useTranslate } from "@tolgee/react"
import { ButtonAddToCart } from "@/components/custom/Product/AddCart"
import { CartIcon, MessageDotsRound } from "@/icons"
import { ProductVariantAttributes } from "../ProductAdditionalAttributes/ProductVariantAttributes"
import { useStorefrontTheme } from "@/lib/theme/storefront-theme-context"
import { isBarebonesTheme } from "@/lib/theme/storefront-theme"

export const ProductDetailsHeader = ({
  product,
  locale,
  user,
  wishlist,
}: {
  product: HttpTypes.StoreProduct & { seller?: SellerProps }
  locale: string
  user: HttpTypes.StoreCustomer | null
  wishlist?: Wishlist[]
}) => {
  const [isAdding, setIsAdding] = useState(false)
  const { allSearchParams } = useGetAllSearchParams()
  const { t } = useTranslate()
  const theme = useStorefrontTheme()
  const barebones = isBarebonesTheme(theme)

  const selectedVariant = buildSelectedVariantOptions(product, allSearchParams)
  const variantId = resolveVariantId(product, selectedVariant)

  const { variant, variantPrice } = getProductPrice({
    product,
    variantId,
  })

  const variantStock =
    product.variants?.find(({ id }) => id === variantId)?.inventory_quantity ||
    0

  const variantHasPrice =
    typeof product.variants?.find(({ id }) => id === variantId)
      ?.calculated_price?.calculated_amount === "number"

  const isAvailable = !!variantStock && variantHasPrice && !isAdding

  const sale =
    product.title
      .replace(/[^a-zA-Z]/g, "")
      .split("")
      .map((s) => s.charCodeAt(0))
      .reduce((a, b) => a + b, 0) % 6

  return (
    <>
      <div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1
              className={
                barebones
                  ? "bb-serif mb-6 text-3xl text-[#2b2118] md:text-[40px] md:leading-[1.15]"
                  : "mb-6 text-3xl font-bold tracking-tight text-gray-900 md:text-[36px] md:leading-[1.2]"
              }
            >
              {product.title}
            </h1>
            <div className="flex items-baseline gap-3 pb-8">
              <span
                className={
                  barebones
                    ? "text-[32px] font-semibold leading-none text-[#2b2118]"
                    : "text-[32px] font-bold leading-none text-gray-900"
                }
              >
                {variantPrice?.calculated_price}
              </span>
              {variantPrice?.calculated_price_number !==
                variantPrice?.original_price_number && (
                <span className="text-lg text-gray-500 line-through">
                  {variantPrice?.original_price}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-row gap-2 pt-1">
            <WishlistButton
              product={product}
              productId={product.id}
              wishlist={wishlist}
              user={user}
              skin={barebones ? "barebones" : "default"}
            />
          </div>
        </div>

        <ProductVariants product={product} selectedVariant={selectedVariant} />

        <div className="space-y-1">
          {variantStock > 0 && variantPrice ? (
            <div className="text-sm font-medium text-green-600">
              {t("product_details.amount_in_stock", { amount: variantStock })}
            </div>
          ) : !variantPrice ? (
            <div className="text-sm font-medium text-red-600">
              {t("product_details.no_region_price")}
            </div>
          ) : (
            <div className="text-sm font-medium text-red-600">
              {t("product_details.out_of_stock")}
            </div>
          )}
          <div className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">
              {sale === 0 ? "500+" : `${sale}000+`}
            </span>{" "}
            {t("product_details.sales")}
          </div>
          <div className="text-sm text-gray-600">
            {t("product_details.shipping_from")}
          </div>
        </div>

        <div className="mt-10 flex w-full flex-col gap-4">
          <ButtonAddToCart
            size="large"
            variant={variant}
            title={product.title}
            locale={locale}
            loading={isAdding}
            setLoading={setIsAdding}
            disabled={!isAvailable}
            thumbnail={product.thumbnail}
            className={
              barebones
                ? "bb-atc h-14 w-full rounded-full bg-[#2b2118] text-base font-medium text-[#f7f4ef] transition-colors hover:bg-[#3e3024] " +
                  (isAvailable ? "" : "opacity-40")
                : "h-14 w-full rounded-xl bg-[#0f1111] text-base font-medium shadow-sm transition-colors hover:bg-gray-800 " +
                  (isAvailable ? "text-white" : "text-gray-400")
            }
          >
            {variantStock && variantHasPrice
              ? t("product_details_header.add_to_cart")
              : !variantHasPrice
                ? t("product_details.no_region_price")
                : t("product_details_header.out_of_stock")}
          </ButtonAddToCart>

          <ButtonAddToCart
            variant={variant}
            title={product.title}
            locale={locale}
            setLoading={setIsAdding}
            disabled={!isAvailable}
            thumbnail={product.thumbnail}
            className={
              barebones
                ? "flex h-14 w-full flex-row items-center justify-center gap-2 rounded-full border border-[#e4ddd2] bg-transparent text-base font-medium text-[#2b2118] transition-colors hover:bg-[#f3eadb] " +
                  (isAvailable ? "" : "opacity-40")
                : "flex h-14 w-full flex-row items-center justify-center gap-2 rounded-xl bg-[#0f1111] text-base font-medium shadow-sm transition-colors hover:bg-gray-800 " +
                  (isAvailable ? "text-white" : "text-gray-400")
            }
            redirectTo="/cart"
          >
            <CartIcon size={16} color={barebones ? "#2b2118" : "#FFF"} />
            <span>{t("product_details.add_to_cart_and_checkout")}</span>
          </ButtonAddToCart>
        </div>

        {user && product.seller && (
          <div className="mb-5 mt-4">
            <Chat
              user={user}
              seller={product.seller}
              className="flex h-12 w-full items-center justify-center rounded-lg border border-gray-300 p-3 transition-colors hover:border-gray-400"
              product={product}
            >
              <MessageDotsRound size={22} color="#16a34a" className="mr-2" />
              <span className="font-medium text-gray-700">
                {t("chat.chat_with_seller")}
              </span>
            </Chat>
          </div>
        )}
      </div>

      <ProductVariantAttributes variant={variant} />
    </>
  )
}
